import { useCallback } from "react";
import { usePathname } from "next/navigation";
import {
  trackPageView,
  trackEvent,
  trackDressDesignEvent,
  trackAuthEvent,
  trackPurchaseEvent,
  trackCheckoutEvent,
  trackSearchEvent,
  trackSocialEvent,
  trackFormSubmission,
  trackDownload,
  trackVideoEvent,
  trackOutboundLink,
  trackNavigation,
  trackDressOrder,
  trackEngagement,
  trackError,
  trackPerformance,
  isGtagAvailable,
  setUserPropertiesOnce,
  trackTutorialBegin as trackTutorialBeginEvent,
  trackCreateStep as trackCreateStepEvent,
  trackPurchaseDetailed as trackPurchaseDetailedUtil,
  type CheckoutTrackingOptions,
} from "@/utils/analytics";

/**
 * Custom hook for Google Analytics tracking
 * Provides easy access to all analytics functions with automatic page view tracking
 */
export function useAnalytics() {
  // Safely resolve pathname in test environments where usePathname may be mocked differently
  const pathname = (() => {
    try {
      const fn: any = usePathname as any;
      if (typeof fn === "function") {
        return fn();
      }
    } catch {}
    if (typeof window !== "undefined") return window.location.pathname || "/";
    return "/";
  })();

  // Track page view when pathname changes
  const trackPageViewCallback = useCallback(
    (url?: string, title?: string) => {
      trackPageView(url || pathname, title);
    },
    [pathname]
  );

  // Dress design specific tracking
  const trackDressDesign = useCallback(
    (
      action: "start" | "complete" | "abandon",
      details?: string,
      stepIndex?: number
    ) => {
      trackDressDesignEvent(action, details, stepIndex);
    },
    []
  );

  // Tutorial/create flow begin
  const trackTutorialBegin = useCallback(() => {
    if (typeof trackTutorialBeginEvent === "function") {
      trackTutorialBeginEvent();
    }
  }, []);

  // Create flow step progress
  const trackCreateStep = useCallback((stepIndex: number) => {
    if (typeof trackCreateStepEvent === "function") {
      trackCreateStepEvent(stepIndex);
    }
  }, []);

  // Set landing/user properties once per session
  const setLandingUserProps = useCallback(
    (props: {
      landing_page: string;
      landing_source?: string | null;
      landing_medium?: string | null;
      landing_campaign?: string | null;
    }) => {
      if (typeof setUserPropertiesOnce === "function") {
        setUserPropertiesOnce(props);
      }
    },
    []
  );

  // Detailed purchase (GA4 items)
  const trackPurchaseDetailed = useCallback(
    (params: {
      transactionId: string;
      value: number;
      currency?: string;
      items: Array<{
        item_id: string;
        item_name: string;
        item_category?: string;
        price: number;
        quantity: number;
      }>;
      affiliation?: string;
      coupon?: string;
    }) => {
      if (typeof trackPurchaseDetailedUtil === "function") {
        trackPurchaseDetailedUtil(params);
      }
    },
    []
  );

  // Authentication tracking
  const trackAuth = useCallback(
    (action: "login" | "logout" | "signup", method?: string) => {
      trackAuthEvent(action, method);
    },
    []
  );

  // Purchase tracking
  const trackPurchase = useCallback(
    (transactionId: string, value: number, currency: string = "USD") => {
      trackPurchaseEvent(transactionId, value, currency);
    },
    []
  );

  // Checkout tracking
  const trackCheckout = useCallback(
    (step: number, value?: number, options?: CheckoutTrackingOptions) => {
      trackCheckoutEvent(step, value, options);
    },
    []
  );

  // Search tracking
  const trackSearch = useCallback(
    (searchTerm: string, resultsCount?: number) => {
      trackSearchEvent(searchTerm, resultsCount);
    },
    []
  );

  // Social media tracking
  const trackSocial = useCallback((platform: string, action: string) => {
    trackSocialEvent(platform, action);
  }, []);

  // Form submission tracking
  const trackForm = useCallback((formName: string, success: boolean = true) => {
    trackFormSubmission(formName, success);
  }, []);

  // File download tracking
  const trackFileDownload = useCallback(
    (fileName: string, fileType: string) => {
      trackDownload(fileName, fileType);
    },
    []
  );

  // Video tracking
  const trackVideo = useCallback(
    (action: "play" | "pause" | "complete", videoTitle: string) => {
      trackVideoEvent(action, videoTitle);
    },
    []
  );

  // Outbound link tracking
  const trackOutbound = useCallback((url: string, linkText?: string) => {
    trackOutboundLink(url, linkText);
  }, []);

  // Internal navigation tracking
  const trackNav = useCallback((destination: string, source?: string) => {
    trackNavigation(destination, source);
  }, []);

  // Dress order tracking
  const trackOrder = useCallback(
    (orderData: {
      orderId: string;
      value: number;
      currency?: string;
      items: Array<{
        itemId: string;
        itemName: string;
        category?: string; // Legacy parameter name
        itemCategory?: string; // GA4-compliant parameter name
        quantity: number;
        price: number;
      }>;
    }) => {
      trackDressOrder(orderData);
    },
    []
  );

  // Engagement tracking
  const trackEngagementMetric = useCallback(
    (metric: "scroll_depth" | "time_on_page" | "bounce", value: number) => {
      trackEngagement(metric, value);
    },
    []
  );

  // Error tracking
  const trackErrorEvent = useCallback(
    (errorType: string, errorMessage: string, fatal: boolean = false) => {
      trackError(errorType, errorMessage, fatal);
    },
    []
  );

  // Performance tracking
  const trackPerformanceMetric = useCallback(
    (metric: string, value: number, unit: string = "ms") => {
      trackPerformance(metric, value, unit);
    },
    []
  );

  // Generic event tracking - GA4 compliant
  const trackCustomEvent = useCallback(
    (eventName: string, params?: Record<string, any>) => {
      trackEvent(eventName, params);
    },
    []
  );

  return {
    // Core functions
    trackPageView: trackPageViewCallback,
    trackEvent: trackCustomEvent,
    isAvailable:
      typeof isGtagAvailable === "function" ? isGtagAvailable() : false,

    // Specific tracking functions
    trackDressDesign,
    trackAuth,
    trackPurchase,
    trackCheckout,
    trackSearch,
    trackSocial,
    trackForm,
    trackFileDownload,
    trackVideo,
    trackOutbound,
    trackNav,
    trackOrder,
    trackEngagementMetric,
    trackErrorEvent,
    trackPerformanceMetric,
    // Funnel helpers
    trackTutorialBegin,
    trackCreateStep,
    setLandingUserProps,
    trackPurchaseDetailed,
  };
}
